/**
 * "Get ready for Section N" prep playlists: per section leader, per section
 * week, a short stored list of puzzles to practice before that section.
 * Built and written by Cloud Functions; read by the SL's dashboard.
 */
import {z} from 'zod';

/** Puzzles a full playlist holds: one tailored slot, then section puzzles. */
export const PREP_PLAYLIST_SIZE = 5;

/** Firestore subcollection (under `course/{courseId}`) of prep playlists. */
export const PREP_PLAYLISTS_COLLECTION = 'prepPlaylists';

/**
 * Course doc field naming the week whose playlists SLs see on the dashboard:
 * a positive week that has built playlists, or absent/null for off.
 */
export const SHOWN_PREP_PLAYLIST_WEEK_FIELD = 'shownPrepPlaylistWeek';

/** The admin callable that builds every SL's playlist for a week. */
export const BUILD_PREP_PLAYLISTS_FUNCTION = 'buildprepplaylists';

/** The callable that builds (if missing) and returns the caller's playlist. */
export const GET_MY_PREP_PLAYLIST_FUNCTION = 'getmyprepplaylist';

/**
 * Why a puzzle is on a playlist: the SL's own tailored puzzle, another
 * section's tailored puzzle standing in for it, or a puzzle tagged for the
 * section.
 */
export const PrepPlaylistReasonSchema = z.enum([
  'own-tailored',
  'other-tailored',
  'section',
]);

/** Why a puzzle is on a playlist. */
export type PrepPlaylistReason = z.infer<typeof PrepPlaylistReasonSchema>;

/** One puzzle on a playlist. */
export const PrepPlaylistItemSchema = z.object({
  /** The puzzle's id under `course/{courseId}/simTeachPuzzles`. */
  puzzleId: z.string().min(1),
  /** Why it's there. */
  reason: PrepPlaylistReasonSchema,
});

/** One puzzle on a playlist. */
export type PrepPlaylistItem = z.infer<typeof PrepPlaylistItemSchema>;

/** One SL's stored playlist for one section week. */
export const PrepPlaylistSchema = z.object({
  /** The section leader it's for. */
  uid: z.string().min(1),
  /** The section it prepares for. */
  sectionWeek: z.number().int().positive(),
  /** The week whose feedback the tailored slot comes from: `sectionWeek - 1`. */
  sourceWeek: z.number().int().nonnegative(),
  /** At most {@link PREP_PLAYLIST_SIZE}; a tailored item, if any, is first. */
  items: z.array(PrepPlaylistItemSchema),
  /** When it was first built, in epoch ms. */
  builtAtMs: z.number(),
  /** When it last changed, in epoch ms. */
  updatedAtMs: z.number(),
});

/** One SL's stored playlist for one section week. */
export type PrepPlaylist = z.infer<typeof PrepPlaylistSchema>;

/** Request for {@link BUILD_PREP_PLAYLISTS_FUNCTION}. */
export interface BuildPrepPlaylistsRequest {
  /** The course. */
  courseId: string;
  /** The week to build for. Building never changes which week SLs see. */
  sectionWeek: number;
}

/** Response from {@link BUILD_PREP_PLAYLISTS_FUNCTION}. */
export const BuildPrepPlaylistsResponseSchema = z.object({
  /** The week built for. */
  sectionWeek: z.number().int().positive(),
  /** SLs the course's active sections serve. */
  sectionLeaders: z.number().int().nonnegative(),
  /** Playlists created or changed by this call. */
  changed: z.number().int().nonnegative(),
});

/** Response from {@link BUILD_PREP_PLAYLISTS_FUNCTION}. */
export type BuildPrepPlaylistsResponse = z.infer<
  typeof BuildPrepPlaylistsResponseSchema
>;

/** Request for {@link GET_MY_PREP_PLAYLIST_FUNCTION}. */
export interface GetMyPrepPlaylistRequest {
  /** The course. */
  courseId: string;
}

/** Response from {@link GET_MY_PREP_PLAYLIST_FUNCTION}. */
export const GetMyPrepPlaylistResponseSchema = z.object({
  /** The week the playlist prepares for, when one could be chosen. */
  sectionWeek: z.number().int().positive().optional(),
  /** The playlist, when one exists or could be built. */
  playlist: PrepPlaylistSchema.optional(),
});

/** Response from {@link GET_MY_PREP_PLAYLIST_FUNCTION}. */
export type GetMyPrepPlaylistResponse = z.infer<
  typeof GetMyPrepPlaylistResponseSchema
>;

/** Counts of one week's built playlists, for the admin panel. */
export const PrepPlaylistWeekStatsSchema = z.object({
  /** The week counted. */
  sectionWeek: z.number().int().positive(),
  /** Playlists stored for it. */
  built: z.number().int().nonnegative(),
  /** SLs the course's active sections serve that week. */
  totalSls: z.number().int().nonnegative(),
  /** Of the built, with the SL's own tailored puzzle first. */
  own: z.number().int().nonnegative(),
  /** Of the built, with another section's tailored puzzle first. */
  borrowed: z.number().int().nonnegative(),
});

/** Counts of one week's built playlists. */
export type PrepPlaylistWeekStats = z.infer<typeof PrepPlaylistWeekStatsSchema>;

/**
 * Path of one SL's playlist. Course-level, so one query finds a week's
 * playlists for spreading borrowed puzzles and for the admin counts.
 *
 * @param args.courseId - The course.
 * @param args.sectionWeek - The section it prepares for.
 * @param args.uid - The section leader.
 * @return `course/{courseId}/prepPlaylists/{sectionWeek}_{uid}`.
 * @throws Never.
 */
export function prepPlaylistDocPath({
  courseId,
  sectionWeek,
  uid,
}: {
  courseId: string;
  sectionWeek: number;
  uid: string;
}): string {
  return `course/${courseId}/${PREP_PLAYLISTS_COLLECTION}/${String(sectionWeek)}_${uid}`;
}

/**
 * The tag a puzzle carries for a section week, e.g. "Section 2".
 *
 * @param sectionWeek - The one-based section week.
 * @return The tag.
 * @throws Never.
 */
export function sectionTag(sectionWeek: number): string {
  return `Section ${String(sectionWeek)}`;
}

/**
 * The week whose playlists SLs see, from the course doc's
 * {@link SHOWN_PREP_PLAYLIST_WEEK_FIELD}.
 *
 * @param setting - The field's raw value.
 * @return The week, or undefined when it's off (absent, null or invalid).
 * @throws Never.
 */
export function shownPrepPlaylistWeek(setting: unknown): number | undefined {
  return typeof setting === 'number' && Number.isInteger(setting) && setting > 0
    ? setting
    : undefined;
}

/** A tailored puzzle another SL could borrow, with how often it's handed out. */
export interface BorrowCandidate {
  /** The puzzle's id. */
  puzzleId: string;
  /** Playlists for the week that already borrow it. */
  assignedCount: number;
}

function sample({
  ids,
  count,
  random,
}: {
  ids: readonly string[];
  count: number;
  random: () => number;
}): string[] {
  const pool = [...ids];
  const picked: string[] = [];
  while (picked.length < count && pool.length > 0) {
    const index = Math.floor(random() * pool.length);
    picked.push(...pool.splice(index, 1));
  }
  return picked;
}

/**
 * The least-assigned borrow candidate, ties broken at random.
 *
 * @param args.candidates - Other sections' tailored puzzles.
 * @param args.random - Returns a number in [0, 1).
 * @return Its puzzle id, or undefined when there are none.
 * @throws Never.
 */
export function pickBorrowedPuzzle({
  candidates,
  random,
}: {
  candidates: readonly BorrowCandidate[];
  random: () => number;
}): string | undefined {
  if (candidates.length === 0) {
    return undefined;
  }
  const fewest = Math.min(...candidates.map(c => c.assignedCount));
  const least = candidates
    .filter(c => c.assignedCount === fewest)
    .map(c => c.puzzleId);
  return sample({ids: least, count: 1, random})[0];
}

/**
 * Fills a playlist's empty or missing slots, keeping what it already holds.
 * Slot one is the SL's own tailored puzzle when there is one (replacing a
 * borrowed one), else the kept or a newly borrowed puzzle; the rest are
 * section puzzles, kept, then sampled at random.
 *
 * @param args.existing - The stored items. Omitted: a new playlist.
 * @param args.ownTailoredIds - The SL's own tailored puzzles from the source
 *   week.
 * @param args.borrowCandidates - Other sections' tailored puzzles from the
 *   source week.
 * @param args.sectionPool - Published puzzles tagged for the section.
 * @param args.random - Returns a number in [0, 1).
 * @return The items, at most {@link PREP_PLAYLIST_SIZE}.
 * @throws Never.
 */
export function fillPrepPlaylist({
  existing = [],
  ownTailoredIds,
  borrowCandidates,
  sectionPool,
  random,
}: {
  existing?: readonly PrepPlaylistItem[];
  ownTailoredIds: readonly string[];
  borrowCandidates: readonly BorrowCandidate[];
  sectionPool: readonly string[];
  random: () => number;
}): PrepPlaylistItem[] {
  const kept = existing.at(0);
  const ownId = ownTailoredIds.at(0);
  let first: PrepPlaylistItem | undefined;
  if (kept?.reason === 'own-tailored') {
    first = kept;
  } else if (ownId !== undefined) {
    first = {puzzleId: ownId, reason: 'own-tailored'};
  } else if (kept?.reason === 'other-tailored') {
    first = kept;
  } else {
    const borrowed = pickBorrowedPuzzle({candidates: borrowCandidates, random});
    first =
      borrowed === undefined
        ? undefined
        : {puzzleId: borrowed, reason: 'other-tailored'};
  }
  const keptSection = existing.filter(
    item => item.reason === 'section' && item.puzzleId !== first?.puzzleId,
  );
  const sectionSlots =
    PREP_PLAYLIST_SIZE - (first === undefined ? 0 : 1) - keptSection.length;
  const used = new Set([
    ...(first === undefined ? [] : [first.puzzleId]),
    ...keptSection.map(item => item.puzzleId),
  ]);
  const added = sample({
    ids: sectionPool.filter(id => !used.has(id)),
    count: Math.max(0, sectionSlots),
    random,
  }).map(puzzleId => ({puzzleId, reason: 'section' as const}));
  return [
    ...(first === undefined ? [] : [first]),
    ...keptSection,
    ...added,
  ].slice(0, PREP_PLAYLIST_SIZE);
}

/**
 * Puts a newly ready own tailored puzzle first, unless slot one already holds
 * an own tailored puzzle. A borrowed first item is replaced; with no tailored
 * item, it goes in front and the last section item drops off a full list.
 *
 * @param args.items - The stored items.
 * @param args.puzzleId - The SL's own tailored puzzle.
 * @return The new items, or undefined when nothing changes.
 * @throws Never.
 */
export function swapInOwnTailored({
  items,
  puzzleId,
}: {
  items: readonly PrepPlaylistItem[];
  puzzleId: string;
}): PrepPlaylistItem[] | undefined {
  const first = items.at(0);
  if (first?.reason === 'own-tailored') {
    return undefined;
  }
  const own: PrepPlaylistItem = {puzzleId, reason: 'own-tailored'};
  const rest = items.filter(
    item => item.reason === 'section' && item.puzzleId !== puzzleId,
  );
  return [own, ...rest].slice(0, PREP_PLAYLIST_SIZE);
}

/**
 * Whether two item lists are the same, in order.
 *
 * @param a - One list.
 * @param b - The other.
 * @return True when they match.
 * @throws Never.
 */
export function isSamePrepPlaylist(
  a: readonly PrepPlaylistItem[],
  b: readonly PrepPlaylistItem[],
): boolean {
  return (
    a.length === b.length &&
    a.every(
      (item, index) =>
        item.puzzleId === b[index]?.puzzleId &&
        item.reason === b[index]?.reason,
    )
  );
}
